/**
 * 診断ロジックサービス
 * アスリート向け職業適性診断のスコア計算とマッチングロジック
 */

export interface AnswerData {
  questionId: string;
  score: number; // 1-5
}

export interface BasicAbilities {
  teamwork: number;
  goalAchievement: number;
  mentalToughness: number;
  growthMindset: number;
  leadership: number;
}

export interface RIASECProfile {
  R: number;
  I: number;
  A: number;
  S: number;
  E: number;
  C: number;
}

export interface WorkValues {
  economicReward: number;
  stability: number;
  workLifeBalance: number;
  socialContribution: number;
  selfGrowth: number;
  relationships: number;
  independence: number;
  achievement: number;
}

export interface PortableSkills {
  communication: number;
  presentation: number;
  negotiation: number;
  listening: number;
  problemIdentification: number;
  analysis: number;
  planning: number;
  execution: number;
}

export interface AssessmentScores {
  basicAbilities: BasicAbilities;
  riasecProfile: RIASECProfile;
  workValues: WorkValues;
  portableSkills: PortableSkills;
}

/**
 * 回答から社会人基礎能力のスコアを計算
 */
export function calculateBasicAbilities(answers: AnswerData[]): BasicAbilities {
  const categories = {
    teamwork: answers.filter(a => a.questionId.startsWith('ba-team-')),
    goalAchievement: answers.filter(a => a.questionId.startsWith('ba-goal-')),
    mentalToughness: answers.filter(a => a.questionId.startsWith('ba-mental-')),
    growthMindset: answers.filter(a => a.questionId.startsWith('ba-growth-')),
    leadership: answers.filter(a => a.questionId.startsWith('ba-leader-')),
  };

  return {
    teamwork: sumValues(categories.teamwork),
    goalAchievement: sumValues(categories.goalAchievement),
    mentalToughness: sumValues(categories.mentalToughness),
    growthMindset: sumValues(categories.growthMindset),
    leadership: sumValues(categories.leadership),
  };
}

/**
 * 回答からRIASECプロファイルを計算
 */
export function calculateRIASECProfile(answers: AnswerData[]): RIASECProfile {
  const categories = {
    R: answers.filter(a => a.questionId.startsWith('riasec-r-')),
    I: answers.filter(a => a.questionId.startsWith('riasec-i-')),
    A: answers.filter(a => a.questionId.startsWith('riasec-a-')),
    S: answers.filter(a => a.questionId.startsWith('riasec-s-')),
    E: answers.filter(a => a.questionId.startsWith('riasec-e-')),
    C: answers.filter(a => a.questionId.startsWith('riasec-c-')),
  };

  return {
    R: sumValues(categories.R),
    I: sumValues(categories.I),
    A: sumValues(categories.A),
    S: sumValues(categories.S),
    E: sumValues(categories.E),
    C: sumValues(categories.C),
  };
}

/**
 * 回答から仕事価値観のスコアを計算
 */
export function calculateWorkValues(answers: AnswerData[]): WorkValues {
  const categories = {
    economicReward: answers.filter(a => a.questionId.startsWith('value-economic-')),
    stability: answers.filter(a => a.questionId.startsWith('value-stability-')),
    workLifeBalance: answers.filter(a => a.questionId.startsWith('value-balance-')),
    socialContribution: answers.filter(a => a.questionId.startsWith('value-social-')),
    selfGrowth: answers.filter(a => a.questionId.startsWith('value-growth-')),
    relationships: answers.filter(a => a.questionId.startsWith('value-relation-')),
    independence: answers.filter(a => a.questionId.startsWith('value-independence-')),
    achievement: answers.filter(a => a.questionId.startsWith('value-achievement-')),
  };

  return {
    economicReward: sumValues(categories.economicReward),
    stability: sumValues(categories.stability),
    workLifeBalance: sumValues(categories.workLifeBalance),
    socialContribution: sumValues(categories.socialContribution),
    selfGrowth: sumValues(categories.selfGrowth),
    relationships: sumValues(categories.relationships),
    independence: sumValues(categories.independence),
    achievement: sumValues(categories.achievement),
  };
}

/**
 * 回答からポータブルスキルのスコアを計算
 */
export function calculatePortableSkills(answers: AnswerData[]): PortableSkills {
  const categories = {
    communication: answers.filter(a => a.questionId.startsWith('skill-comm-')),
    presentation: answers.filter(a => a.questionId.startsWith('skill-present-')),
    negotiation: answers.filter(a => a.questionId.startsWith('skill-nego-')),
    listening: answers.filter(a => a.questionId.startsWith('skill-listen-')),
    problemIdentification: answers.filter(a => a.questionId.startsWith('skill-problem-')),
    analysis: answers.filter(a => a.questionId.startsWith('skill-analysis-')),
    planning: answers.filter(a => a.questionId.startsWith('skill-planning-')),
    execution: answers.filter(a => a.questionId.startsWith('skill-execution-')),
  };

  return {
    communication: sumValues(categories.communication),
    presentation: sumValues(categories.presentation),
    negotiation: sumValues(categories.negotiation),
    listening: sumValues(categories.listening),
    problemIdentification: sumValues(categories.problemIdentification),
    analysis: sumValues(categories.analysis),
    planning: sumValues(categories.planning),
    execution: sumValues(categories.execution),
  };
}

/**
 * 全ての診断スコアを計算
 */
export function calculateAllScores(answers: AnswerData[]): AssessmentScores {
  return {
    basicAbilities: calculateBasicAbilities(answers),
    riasecProfile: calculateRIASECProfile(answers),
    workValues: calculateWorkValues(answers),
    portableSkills: calculatePortableSkills(answers),
  };
}

/**
 * スコアを0-100%に正規化するヘルパー
 * 社会人基礎能力: 4問×5点=20点満点
 * RIASEC: 6問×5点=30点満点
 * ポータブルスキル: 3問×5点=15点満点
 */
function normalizeToPercent(score: number, maxScore: number): number {
  return (score / maxScore) * 100;
}

/**
 * 強みTOP3を抽出（正規化後のスコアで比較）
 * RIASECは「興味の方向性」であり能力ではないため、強みの対象外とする
 */
export function extractStrengths(scores: AssessmentScores): string[] {
  const allScores: Array<{ category: string; score: number }> = [
    // 社会人基礎能力（20点満点）
    { category: 'チームワーク力', score: normalizeToPercent(scores.basicAbilities.teamwork, 20) },
    { category: '目標達成力', score: normalizeToPercent(scores.basicAbilities.goalAchievement, 20) },
    { category: 'メンタルタフネス', score: normalizeToPercent(scores.basicAbilities.mentalToughness, 20) },
    { category: '自己成長意欲', score: normalizeToPercent(scores.basicAbilities.growthMindset, 20) },
    { category: 'リーダーシップ', score: normalizeToPercent(scores.basicAbilities.leadership, 20) },

    // ポータブルスキル（15点満点）
    { category: 'コミュニケーション力', score: normalizeToPercent(scores.portableSkills.communication, 15) },
    { category: 'プレゼンテーション力', score: normalizeToPercent(scores.portableSkills.presentation, 15) },
    { category: '交渉力', score: normalizeToPercent(scores.portableSkills.negotiation, 15) },
    { category: '傾聴力', score: normalizeToPercent(scores.portableSkills.listening, 15) },
    { category: '問題発見力', score: normalizeToPercent(scores.portableSkills.problemIdentification, 15) },
    { category: '分析力', score: normalizeToPercent(scores.portableSkills.analysis, 15) },
    { category: '計画力', score: normalizeToPercent(scores.portableSkills.planning, 15) },
    { category: '実行力', score: normalizeToPercent(scores.portableSkills.execution, 15) },
  ];

  // スコアの高い順にソートしてTOP3を抽出
  return allScores
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(item => item.category);
}

/**
 * 発展領域を抽出（正規化後のスコアで比較）
 */
export function extractDevelopmentAreas(scores: AssessmentScores): string[] {
  const allScores: Array<{ category: string; score: number }> = [
    // 社会人基礎能力（20点満点）
    { category: 'チームワーク力', score: normalizeToPercent(scores.basicAbilities.teamwork, 20) },
    { category: '目標達成力', score: normalizeToPercent(scores.basicAbilities.goalAchievement, 20) },
    { category: 'メンタルタフネス', score: normalizeToPercent(scores.basicAbilities.mentalToughness, 20) },
    { category: '自己成長意欲', score: normalizeToPercent(scores.basicAbilities.growthMindset, 20) },
    { category: 'リーダーシップ', score: normalizeToPercent(scores.basicAbilities.leadership, 20) },

    // ポータブルスキル（15点満点）
    { category: 'コミュニケーション力', score: normalizeToPercent(scores.portableSkills.communication, 15) },
    { category: 'プレゼンテーション力', score: normalizeToPercent(scores.portableSkills.presentation, 15) },
    { category: '交渉力', score: normalizeToPercent(scores.portableSkills.negotiation, 15) },
    { category: '傾聴力', score: normalizeToPercent(scores.portableSkills.listening, 15) },
    { category: '問題発見力', score: normalizeToPercent(scores.portableSkills.problemIdentification, 15) },
    { category: '分析力', score: normalizeToPercent(scores.portableSkills.analysis, 15) },
    { category: '計画力', score: normalizeToPercent(scores.portableSkills.planning, 15) },
    { category: '実行力', score: normalizeToPercent(scores.portableSkills.execution, 15) },
  ];

  // スコアの低い順にソートして下位2つを抽出
  return allScores
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
    .map(item => item.category);
}

/**
 * アスリート経験が活かせるポイントを抽出
 * 各能力を正規化（0-100%）し、75%以上を「強み」として表示
 */
export function extractAthleteAdvantages(
  scores: AssessmentScores,
  yearsOfExperience: number
): string[] {
  const advantages: string[] = [];

  // 社会人基礎能力（20点満点 → 75% = 15点以上）
  if (scores.basicAbilities.teamwork >= 15) {
    advantages.push('チームで協力して成果を出す力が優れています');
  }
  if (scores.basicAbilities.goalAchievement >= 15) {
    advantages.push('目標に向かって計画的に取り組む力が強みです');
  }
  if (scores.basicAbilities.mentalToughness >= 15) {
    advantages.push('プレッシャーに強く、困難な状況でも冷静に対処できます');
  }
  if (scores.basicAbilities.growthMindset >= 15) {
    advantages.push('常に成長を求める向上心が仕事でも活かせます');
  }
  if (scores.basicAbilities.leadership >= 15) {
    advantages.push('競技経験で培ったリーダーシップを発揮できます');
  }

  // ポータブルスキル（15点満点 → 75% = 12点以上）（上位のものだけ）
  if (scores.portableSkills.execution >= 12) {
    advantages.push('スポーツで培った実行力と粘り強さが武器になります');
  }
  if (scores.portableSkills.communication >= 12) {
    advantages.push('コミュニケーション力が高く、周囲と連携する力があります');
  }

  // 経験年数が長い場合
  if (yearsOfExperience >= 10) {
    advantages.push('長年のスポーツ経験で培った継続力と忍耐力が強みです');
  }

  // 何も該当しない場合のフォールバック
  if (advantages.length === 0) {
    advantages.push('スポーツで培った規律と努力する姿勢が仕事に活かせます');
  }

  return advantages;
}

/**
 * コサイン類似度を計算（RIASECプロファイルの比較用）
 */
export function calculateCosineSimilarity(
  profile1: RIASECProfile,
  profile2: RIASECProfile
): number {
  const vec1 = [profile1.R, profile1.I, profile1.A, profile1.S, profile1.E, profile1.C];
  const vec2 = [profile2.R, profile2.I, profile2.A, profile2.S, profile2.E, profile2.C];

  const dotProduct = vec1.reduce((sum, val, i) => sum + val * vec2[i], 0);
  const magnitude1 = Math.sqrt(vec1.reduce((sum, val) => sum + val * val, 0));
  const magnitude2 = Math.sqrt(vec2.reduce((sum, val) => sum + val * val, 0));

  if (magnitude1 === 0 || magnitude2 === 0) return 0;

  return dotProduct / (magnitude1 * magnitude2);
}

/**
 * ユーティリティ: 回答の値を合計
 */
function sumValues(answers: AnswerData[]): number {
  return answers.reduce((sum, answer) => sum + answer.score, 0);
}

/**
 * 偏差値を計算
 */
export function calculateDeviation(score: number, mean: number, stdDev: number): number {
  if (stdDev === 0) return 50; // 標準偏差が0の場合は平均値（50）を返す
  return 50 + ((score - mean) / stdDev) * 10;
}
